import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter, Routes, Route } from 'react-router';
import 'instantsearch.css/themes/algolia.css';
import { Content, Context, ErrorBoundary, Loading, IntercomUpdate, Toast } from 'components/common';
import { OnboardingModal } from 'components/onboarding';
import { Sidebar, Topbar } from 'layouts';
import { configureStore } from 'state';
import './bootstrap';
import { runtimeConfig } from './runtime-config';

const AI = lazy(() => import('./layouts/AI' /* webpackChunkName: "ai", webpackPrefetch: true */));

const AltLending = lazy(
  () => import('./layouts/AltLending' /* webpackChunkName: "alt-lending", webpackPrefetch: true */)
);

const Guidelines = lazy(
  () => import('./layouts/Guidelines' /* webpackChunkName: "guidelines", webpackPrefetch: true */)
);

const Lenders = lazy(() => import('./layouts/Lenders' /* webpackChunkName: "lenders", webpackPrefetch: true */));

const Rates = lazy(() => import('./layouts/Rates' /* webpackChunkName: "rates", webpackPrefetch: true */));

const Scenarios = lazy(() => import('./layouts/Scenarios' /* webpackChunkName: "scenarios", webpackPrefetch: true */));

const Settings = lazy(() => import('./layouts/Settings' /* webpackChunkName: "settings", webpackPrefetch: true */));

const TeamManagement = lazy(
  () => import('./layouts/TeamManagement' /* webpackChunkName: "team-management", webpackPrefetch: true */)
);

const Teams = lazy(() => import('./layouts/Teams' /* webpackChunkName: "teams", webpackPrefetch: true */));

const { store, persistor } = configureStore();

const App = () => (
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter>
          <IntercomUpdate />
          <GoogleReCaptchaProvider reCaptchaKey={runtimeConfig.recaptchaSiteKeyV3}>
            <Sidebar />
            <OnboardingModal />
            <Context>
              <Topbar />
              <Content>
                {/* We use this inner boundary to maintain logo/nav UI in case of errors */}
                <ErrorBoundary>
                  <Suspense fallback={<Loading pastDelay />}>
                    <Routes>
                      <Route path="/ai/*" element={<AI />} />
                      <Route path="/alternative-lending/*" element={<AltLending />} />
                      <Route path="/guidelines/*" element={<Guidelines />} />
                      <Route path="/lenders/*" element={<Lenders />} />
                      <Route path="/rates/*" element={<Rates />} />
                      <Route path="/scenarios/*" element={<Scenarios />} />
                      <Route path="/settings/*" element={<Settings />} />
                      <Route path="/team-management/*" element={<TeamManagement />} />
                      <Route path="/teams/*" element={<Teams />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Content>
              <Toast />
            </Context>
          </GoogleReCaptchaProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </ErrorBoundary>
);

const root = createRoot(document.getElementById('root') || document.createElement('div'));

root.render(<App />);

export default App;
